import { useEffect, useState } from "react";
import type { AeaExerciseModel, TeacherTestDetails } from "p6m-p6u";
import { useGeneralContext } from "../context/AppGeneralContext";
import { useBookContext } from "../context/BookContext";
import { collectActivityBooks } from "../helpers/BookAvailability";

/**
 * Resolves the band infos needed to drop activities from books the current flow excludes, and
 * reports when they are in: filtering before that renders content which then disappears, and
 * counts activities the teacher can't actually pick.
 */
export const useActivityBookAvailability = (
    tests: Array<TeacherTestDetails>,
    trainings: Array<AeaExerciseModel>,
    isEnabled: boolean
) => {
    const { userUsedBooksInfo, isBookBlocked, loadBandInfosForActivitiesIntoState } = useBookContext();
    const { userId } = useGeneralContext();

    const [areBookInfosResolved, setAreBookInfosResolved] = useState(false);

    const books = isEnabled ? collectActivityBooks(tests, trainings, userId) : [];
    // The lists arrive in one go, so the ids only change when a list is (re)loaded.
    const bookIdsKey = books.map((book) => book.id).join(",");

    useEffect(() => {
        if (!isEnabled) return;

        // A list reloading mid-flight starts a second run; only the last one may report "resolved".
        let isLatestRun = true;
        setAreBookInfosResolved(false);
        loadBandInfosForActivitiesIntoState(books).finally(() => {
            if (isLatestRun) setAreBookInfosResolved(true);
        });
        return () => {
            isLatestRun = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabled, bookIdsKey]);

    const isBookOfActivityBlocked = (bookId?: string) => !!bookId && isBookBlocked(userUsedBooksInfo[bookId]);

    return { areBookInfosResolved, isBookOfActivityBlocked };
};
